import { CartAddEvent } from "@theme/events";
import { sectionRenderer } from "@theme/section-renderer";
import { fetchConfig } from "@theme/utilities";

/**
 * Collection Tabs section: tab scroll navigation + add to cart.
 */
class CollectionTabsSection extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #scrollRaf = null;

  /** @type {number} */
  #scrollOffset = 40;

  /** @type {boolean} */
  #ignoreScrollSpy = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #ignoreScrollSpyTimeout = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#collapseMobileBundleAccordions();
    this.#updateScrollMargin();

    this.querySelectorAll("[data-collection-tabs-tab]").forEach((tab) => {
      tab.addEventListener("click", this.#onTabClick, { signal });
    });

    this.querySelectorAll("[data-collection-tabs-atc]").forEach((button) => {
      button.addEventListener("click", this.#onAddToCart, { signal });
    });

    this.querySelectorAll("[data-collection-tabs-subscribe]").forEach((button) => {
      button.addEventListener("click", (event) => this.#onAddToCart(event, true), { signal });
    });

    this.addEventListener("change", this.#onSubscriptionToggleChange, { signal });
    this.addEventListener("subscription-dropdown:change", this.#onSubscriptionSelectionChange, {
      signal,
    });

    window.addEventListener("scroll", this.#onScroll, { signal, passive: true });
    window.addEventListener("resize", this.#onResize, { signal });
    this.#syncActiveTabFromScroll();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    if (this.#scrollRaf != null) {
      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = null;
    }
    if (this.#ignoreScrollSpyTimeout != null) {
      clearTimeout(this.#ignoreScrollSpyTimeout);
      this.#ignoreScrollSpyTimeout = null;
    }
  }

  #isMobile = () => window.matchMedia("(max-width: 749px)").matches;

  #collapseMobileBundleAccordions() {
    if (!this.#isMobile()) return;

    this.querySelectorAll(".collection-tabs-card__box[open]").forEach((details) => {
      details.removeAttribute("open");
    });
  }

  #updateScrollMargin = () => {
    let offset = 40;

    if (this.#isMobile()) {
      const tabsBar = this.querySelector(".collection-tabs__tabs-bar");
      if (tabsBar instanceof HTMLElement) {
        offset += tabsBar.offsetHeight;
      }
    } else {
      const headerHeight =
        parseFloat(getComputedStyle(document.body).getPropertyValue("--header-group-height")) || 0;
      offset += headerHeight;
    }

    this.#scrollOffset = offset;
    const nextMargin = `${offset}px`;
    this.style.setProperty("--ct-scroll-margin", nextMargin);
    return nextMargin;
  };

  #onResize = () => {
    this.#updateScrollMargin();
    this.#syncActiveTabFromScroll();
  };

  #onScroll = () => {
    if (this.#scrollRaf != null) return;

    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#syncActiveTabFromScroll();
    });
  };

  /**
   * Pick the last collection whose top has crossed the sticky offset line.
   * More reliable than IntersectionObserver during fast scrolls.
   */
  #syncActiveTabFromScroll = () => {
    if (this.#ignoreScrollSpy) return;

    const collections = this.querySelectorAll(".collection-tabs__collection");
    if (!collections.length) return;

    const marker = this.#scrollOffset + 1;
    /** @type {Element | null} */
    let active = null;

    collections.forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      if (section.getBoundingClientRect().top <= marker) {
        active = section;
      }
    });

    if (!active) {
      active = collections[0] ?? null;
    }

    if (!(active instanceof HTMLElement) || !active.id) return;

    const tab = this.querySelector(`[data-collection-tabs-tab][data-target="${active.id}"]`);
    if (tab instanceof HTMLElement) {
      this.#setSelectedTab(tab);
    }
  };

  /**
   * @param {Event} event
   */
  #onTabClick = (event) => {
    event.preventDefault();
    const tab = /** @type {HTMLElement} */ (event.currentTarget);
    const targetId = tab.getAttribute("data-target");
    if (!targetId) return;

    const target = this.querySelector(`#${CSS.escape(targetId)}`);
    if (!(target instanceof HTMLElement)) return;

    this.#setSelectedTab(tab);
    this.#updateScrollMargin();
    this.#pauseScrollSpy();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  #pauseScrollSpy() {
    this.#ignoreScrollSpy = true;
    if (this.#ignoreScrollSpyTimeout != null) {
      clearTimeout(this.#ignoreScrollSpyTimeout);
    }
    this.#ignoreScrollSpyTimeout = setTimeout(() => {
      this.#ignoreScrollSpy = false;
      this.#ignoreScrollSpyTimeout = null;
      this.#syncActiveTabFromScroll();
    }, 800);
  }

  /**
   * @param {HTMLElement} activeTab
   */
  #setSelectedTab(activeTab) {
    this.querySelectorAll("[data-collection-tabs-tab]").forEach((tab) => {
      const isActive = tab === activeTab;
      if (tab.classList.contains("selected") === isActive) {
        if (tab.getAttribute("aria-selected") !== (isActive ? "true" : "false")) {
          tab.setAttribute("aria-selected", isActive ? "true" : "false");
        }
        return;
      }
      tab.classList.toggle("selected", isActive);
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
    });
  }

  /**
   * @returns {Promise<HTMLElement | null>}
   */
  async #getCartDrawer() {
    try {
      await customElements.whenDefined("cart-drawer-component");
    } catch {
      return null;
    }

    const drawer = document.querySelector("cart-drawer-component");
    return drawer instanceof HTMLElement ? drawer : null;
  }

  async #openCartDrawer() {
    const drawer = await this.#getCartDrawer();
    if (!drawer) return;

    if (
      typeof (/** @type {{ open?: () => void | Promise<void> }} */ (drawer).open) === "function"
    ) {
      await /** @type {{ open: () => void | Promise<void> }} */ (drawer).open();
    }
  }

  /**
   * @param {Event} event
   * @param {boolean} [isSubscription]
   */
  #onAddToCart = async (event, isSubscription = false) => {
    event.preventDefault();
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    if (button.disabled) return;

    const variantId = button.getAttribute("data-variant-id");
    const productId = button.getAttribute("data-product-id");
    if (!variantId) return;

    const originalLabel = button.textContent;
    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    try {
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", "1");

      if (isSubscription) {
        const card = button.closest(".collection-tabs-card");
        const selectedOption = card?.querySelector(
          '[data-dropdown-select] [data-option][aria-selected="true"]'
        );
        const sellingPlanId = selectedOption?.getAttribute("data-option");
        if (sellingPlanId) {
          formData.append("selling_plan", sellingPlanId);
        }
      }

      const cartItemsComponents = document.querySelectorAll("cart-items-component");
      /** @type {string[]} */
      const sectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });
      if (sectionIds.length) {
        formData.append("sections", sectionIds.join(","));
        formData.append("sections_url", window.location.pathname);
      }

      const fetchCfg = fetchConfig("javascript", { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: "application/json",
        },
      });
      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || data.message || "Could not add to cart");
      }

      let itemCount = Number(data.item_count);
      if (!Number.isFinite(itemCount) || itemCount < 1) {
        try {
          const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
          itemCount = Number(cart.item_count) || 1;
        } catch {
          itemCount = 1;
        }
      }

      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: "collection-tabs-section",
          itemCount,
          productId: productId || undefined,
          variantId,
        })
      );

      // Single header remorph before open (handler skips remorph for this source).
      const sectionId = sectionIds[0];
      if (sectionId) {
        await sectionRenderer.renderSection(sectionId, { cache: false });
      }

      await this.#openCartDrawer();

      button.textContent = Theme.translations?.added || "Added";
      setTimeout(() => {
        button.textContent = originalLabel;
        button.disabled = false;
        button.removeAttribute("aria-busy");
      }, 1500);
    } catch (error) {
      console.error(error);
      button.disabled = false;
      button.removeAttribute("aria-busy");
      button.textContent = originalLabel;
    }
  };

  /**
   * @param {Event} event
   */
  #onSubscriptionToggleChange = (event) => {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;
    if (!checkbox.classList.contains("subscription-toggle-checkbox")) return;

    const card = checkbox.closest(".collection-tabs-card");
    if (card instanceof HTMLElement) this.#updateCardPrice(card);
  };

  /**
   * @param {Event} event
   */
  #onSubscriptionSelectionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const card = target.closest(".collection-tabs-card");
    if (card instanceof HTMLElement) this.#updateCardPrice(card);
  };

  /**
   * @param {HTMLElement} card
   */
  #updateCardPrice(card) {
    const priceEl = card.querySelector(".collection-tabs-card__price");
    if (!(priceEl instanceof HTMLElement)) return;

    const subscriptionPriceEl = priceEl.querySelector(".collection-tabs-card__price-subscription");
    if (!(subscriptionPriceEl instanceof HTMLElement)) return;

    const basePrice = Number(priceEl.getAttribute("data-price"));
    if (!Number.isFinite(basePrice)) return;

    const checkbox = card.querySelector(".subscription-toggle-checkbox");
    const isSubscribed = checkbox instanceof HTMLInputElement && checkbox.checked;

    if (!isSubscribed) {
      subscriptionPriceEl.textContent = "";
      subscriptionPriceEl.hidden = true;
      return;
    }

    const selectedOption = card.querySelector(
      '[data-dropdown-select] [data-option][aria-selected="true"]'
    );
    const priceAdjustmentsValue = Number(
      selectedOption?.getAttribute("data-price-adjustments-value")
    );
    if (!Number.isFinite(priceAdjustmentsValue)) return;

    const discountedCents = basePrice * (1 - priceAdjustmentsValue / 100);
    subscriptionPriceEl.textContent = this.#formatMoney(discountedCents);
    subscriptionPriceEl.hidden = false;
  }

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
      cents / 100
    );
  }
}

if (!customElements.get("collection-tabs-section")) {
  customElements.define("collection-tabs-section", CollectionTabsSection);
}

/**
 * Pseudo dropdown that drives the selling-plan `[data-dropdown-select]` list
 * rendered by snippets/pf-subscription-control.liquid.
 */
class SubscriptionDropdown extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const toggle = this.#getToggle();
    if (toggle) {
      toggle.setAttribute("aria-expanded", "false");
      toggle.addEventListener("click", this.#onToggleClick, { signal });
    }

    this.querySelectorAll("[data-option]").forEach((option) => {
      option.addEventListener("click", this.#onOptionClick, { signal });
    });

    document.addEventListener("click", this.#onDocumentClick, { signal });
    document.addEventListener("keydown", this.#onKeydown, { signal });

    this.#fetchRechargeProductData();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  async #fetchRechargeProductData() {
    const productId = this.getAttribute("product-id");

    try {
      const rechargeProductData = await recharge.cdn.getCDNProduct(productId);
      console.log(rechargeProductData);
      this.#hideUnavailableSellingPlans(rechargeProductData);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Hides selling-plan options that have no match under selling_plan_groups
   * in the Recharge CDN product data.
   * @param {any} rechargeProductData
   */
  #hideUnavailableSellingPlans(rechargeProductData) {
    /** @type {Map<string, any>} */
    const sellingPlanPriceAdjustments = new Map();

    /** @type {any[]} */
    (rechargeProductData.selling_plan_groups || []).forEach((group) => {
      /** @type {any[]} */
      (group.selling_plans || []).forEach((plan) => {
        sellingPlanPriceAdjustments.set(String(plan.selling_plan_id), plan.price_adjustments_value);
      });
    });

    /** @type {HTMLElement | null} */
    let selectedOption = null;

    this.querySelectorAll("[data-option]").forEach((option) => {
      if (!(option instanceof HTMLElement)) return;
      const planId = option.getAttribute("data-option") || "";
      const isAvailable = sellingPlanPriceAdjustments.has(planId);
      option.hidden = !isAvailable;

      const priceAdjustmentsValue = sellingPlanPriceAdjustments.get(planId);
      if (priceAdjustmentsValue !== undefined && priceAdjustmentsValue !== null) {
        option.setAttribute("data-price-adjustments-value", priceAdjustmentsValue);
      }

      if (option.getAttribute("aria-selected") === "true") {
        selectedOption = option;
      }
    });

    if (!selectedOption?.hidden) return;

    const nextOption = Array.from(this.querySelectorAll("[data-option]")).find(
      (option) => option instanceof HTMLElement && !option.hidden
    );

    if (nextOption instanceof HTMLElement) {
      this.#selectOption(nextOption);
    } else {
      selectedOption.setAttribute("aria-selected", "false");
    }
  }

  /** @returns {HTMLElement | null} */
  #getToggle() {
    const toggle = this.querySelector("[data-dropdown-toggle]");
    return toggle instanceof HTMLElement ? toggle : null;
  }

  /** @returns {HTMLElement | null} */
  #getList() {
    const list = this.querySelector("[data-dropdown-select]");
    return list instanceof HTMLElement ? list : null;
  }

  #isOpen() {
    return this.#getList()?.classList.contains("active") ?? false;
  }

  #open() {
    const list = this.#getList();
    if (list) {
      list.hidden = false;
      list.classList.add("active");
    }
    this.#getToggle()?.setAttribute("aria-expanded", "true");
  }

  #close() {
    const list = this.#getList();
    if (list) {
      list.classList.remove("active");
      list.hidden = true;
    }
    this.#getToggle()?.setAttribute("aria-expanded", "false");
  }

  /**
   * @param {MouseEvent} event
   */
  #onToggleClick = (event) => {
    event.preventDefault();
    if (this.#isOpen()) {
      this.#close();
    } else {
      this.#open();
    }
  };

  /**
   * @param {Event} event
   */
  #onOptionClick = (event) => {
    const option = event.currentTarget;
    if (!(option instanceof HTMLElement)) return;
    this.#selectOption(option, { close: true });
  };

  /**
   * @param {HTMLElement} option
   * @param {{ close?: boolean }} [config]
   */
  #selectOption(option, { close = false } = {}) {
    const planId = option.getAttribute("data-option");
    const planName = option.getAttribute("data-value") || option.textContent?.trim() || "";

    this.querySelectorAll("[data-option]").forEach((sibling) => {
      sibling.setAttribute("aria-selected", sibling === option ? "true" : "false");
    });

    const label = this.querySelector("[data-dropdown-toggle-label]");
    if (label) label.textContent = planName;

    const toggle = this.#getToggle();
    if (toggle && planName) toggle.setAttribute("aria-label", planName);

    if (planId) this.setAttribute("data-selected-id", planId);

    if (close) {
      this.#close();
      toggle?.focus();
    }

    this.dispatchEvent(
      new CustomEvent("subscription-dropdown:change", {
        bubbles: true,
        detail: { sellingPlanId: planId, sellingPlanName: planName },
      })
    );
  }

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#isOpen()) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#close();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== "Escape" || !this.#isOpen()) return;
    this.#close();
    this.#getToggle()?.focus();
  };
}

if (!customElements.get("subscription-dropdown")) {
  customElements.define("subscription-dropdown", SubscriptionDropdown);
}
